import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { 
  Users, 
  FileText, 
  TrendingUp, 
  AlertTriangle,
  Calendar,
  Activity,
  BarChart3,
  PieChart,
  Plus,
  Edit,
  Trash2,
  Download,
  User,
  Shield,
  Clock,
  Target,
  ArrowRight,
  Eye,
  Settings,
  Filter,
  X
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { useApiAuth } from '../../hooks/useApiAuth';
import { laceToolAPI, auditLogAPI, reportsAPI, AuditLog, Assessment, Facility, HealthCareProvider } from '../../services/api';
import { authenticatedLaceToolAPI } from '../../services/authenticatedApi';
import { format, formatDistanceToNow } from 'date-fns';
import { SkeletonDashboard } from '../Skeleton/SkeletonLoader';

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const WelcomeSection = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(30, 64, 175, 0.3);
  display: flex;
  align-items: center;
  gap: 24px;
`;

const WelcomeLogo = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 12px;
  background: white;
  padding: 8px;
  flex-shrink: 0;
`;

const WelcomeContent = styled.div`
  flex: 1;
`;

const WelcomeTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const WelcomeSubtitle = styled.p`
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
`;

const WelcomeActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const DownloadButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  background: ${props => props.color};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

const StatChange = styled.div`
  font-size: 12px;
  color: #dc2626;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;

  &[data-positive='true'] {
    color: #059669;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ChartContainer = styled.div`
  height: 300px;
  width: 100%;
`;

const ReadmissionDistributionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 20px;
`;

const ReadmissionCard = styled.div<{ risk: 'low' | 'moderate' | 'high' }>`
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  background: ${props => {
    switch (props.risk) {
      case 'low': return '#f0fdf4';
      case 'moderate': return '#fef3c7';
      case 'high': return '#fef2f2';
      default: return '#f9fafb';
    }
  }};
  border: 1px solid ${props => {
    switch (props.risk) {
      case 'low': return '#bbf7d0';
      case 'moderate': return '#fde68a';
      case 'high': return '#fecaca';
      default: return '#e5e7eb';
    }
  }};
`;

const ReadmissionValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const ReadmissionLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RecentActivity = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ActivityIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #3b82f6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityText = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  margin-bottom: 2px;
`;

const ActivityMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
`;

const ActivityUser = styled.span`
  font-weight: 500;
  color: #374151;
`;

const ActivityTime = styled.span`
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 16px;
  color: #6b7280;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #111827;
  
  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-color: #3b82f6;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #111827;
  
  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-color: #3b82f6;
  }
`;

const FilterActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`;

const ActiveFilterTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const RemoveFilterButton = styled.button`
  background: none;
  border: none;
  color: #1e40af;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #dc2626;
  }
`;

const QuickActionsSection = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: #374151;
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    border-color: #3b82f6;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const QuickActionContent = styled.div`
  flex: 1;
`;

const QuickActionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const QuickActionDescription = styled.div`
  font-size: 12px;
  opacity: 0.8;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const MetricTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const MetricSubtext = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const TrendIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;

  &[data-positive='true'] {
    color: #059669;
  }
`;

interface DashboardStats {
  overall: {
    totalAssessments: number;
    averageScore: number;
    averageReadmissionProbability?: number;
    minScore: number;
    maxScore: number;
    lowReadmission: number;
    moderateReadmission: number;
    highReadmission: number;
  };
  topFacilities: Array<{
    _id: string;
    count: number;
    averageScore: number;
  }>;
  doctorBreakdown?: Array<{
    _id: string;
    count: number;
    averageScore: number;
    lowReadmission: number;
    moderateReadmission: number;
    highReadmission: number;
  }>;
}

interface RecentActivityItem {
  id: string;
  type: 'assessment' | 'audit';
  icon: React.ReactNode;
  text: string;
  time: string;
  timestamp: string;
  user?: string;
}

const COLORS = {
  lowReadmission: '#10b981',
  moderateReadmission: '#f59e0b',
  highReadmission: '#ef4444'
};

const Dashboard: React.FC = () => {
  const { getAccessToken } = useApiAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [doctors, setDoctors] = useState<HealthCareProvider[]>([]);
  const [filters, setFilters] = useState({
    facilityName: '',
    doctorName: '',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>({});

  const getActivityIcon = (action: string, resource: string) => {
    switch (action) {
      case 'CREATE':
        return <Plus size={16} />;
      case 'UPDATE':
        return <Edit size={16} />;
      case 'DELETE':
        return <Trash2 size={16} />;
      case 'DATA_EXPORT':
        return <Download size={16} />;
      case 'LOGIN':
      case 'LOGOUT':
        return <User size={16} />;
      default:
        return resource === 'ASSESSMENT' ? <FileText size={16} /> : <Activity size={16} />;
    }
  };

  const formatActivityText = (log: AuditLog) => {
    const { action, resource, details } = log;
    
    switch (action) {
      case 'CREATE':
        if (resource === 'ASSESSMENT' && details.patientName) {
          return `New assessment created for ${details.patientName}${details.facilityName ? ` at ${details.facilityName}` : ''}`;
        }
        return `New ${resource.toLowerCase()} created`;
      case 'UPDATE':
        if (resource === 'ASSESSMENT' && details.patientName) {
          return `Assessment updated for ${details.patientName}`;
        }
        return `${resource.toLowerCase()} updated`;
      case 'DELETE':
        if (resource === 'ASSESSMENT' && details.patientName) {
          return `Assessment deleted for ${details.patientName}`;
        }
        return `${resource.toLowerCase()} deleted`;
      case 'DATA_EXPORT':
        return 'Data exported successfully';
      case 'LOGIN':
        return 'User logged in';
      case 'LOGOUT':
        return 'User logged out';
      case 'LOGIN_FAILED':
        return 'Failed login attempt';
      default:
        return `${action.toLowerCase()} action on ${resource.toLowerCase()}`;
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'new-assessment':
        window.location.href = '/assessments/new';
        break;
      case 'view-assessments':
        window.location.href = '/assessments';
        break;
      case 'audit-logs':
        window.location.href = '/audit-logs';
        break;
      case 'export-data':
        handleDownloadDashboardPDF();
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleDownloadDashboardPDF = async () => {
    const loadingToast = toast.loading('Processing PDF download...');
    
    try {
      console.log('Starting dashboard PDF download');
      
      // Use applied filters for the PDF download
      const filters = Object.keys(appliedFilters).length > 0 ? appliedFilters : undefined;
      
      const blob = await authenticatedLaceToolAPI.downloadDashboardPDF(filters);
      
      if (!blob || blob.size === 0) {
        throw new Error('Received empty or invalid PDF file');
      }
      
      console.log('Dashboard PDF blob received, size:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      link.download = `LACE_Dashboard_Report_${date}.pdf`;
      
      console.log('Downloading file:', link.download);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Dashboard PDF download completed successfully');
      toast.success('Dashboard PDF download started!', { id: loadingToast });
    } catch (error) {
      console.error('Error downloading dashboard PDF:', error);
      toast.error('Failed to download dashboard PDF. Please try again.', { id: loadingToast });
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const applyFilters = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const newAppliedFilters: Record<string, string> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        newAppliedFilters[key] = value;
      }
    });
    setAppliedFilters(newAppliedFilters);
    fetchDashboardData(newAppliedFilters);
  };

  const clearFilters = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    setFilters({
      facilityName: '',
      doctorName: '',
      startDate: '',
      endDate: ''
    });
    setAppliedFilters({});
    fetchDashboardData({});
  };

  const removeFilter = (key: string) => {
    const newFilters = { ...filters };
    newFilters[key as keyof typeof filters] = '';
    setFilters(newFilters);
    
    const newAppliedFilters = { ...appliedFilters };
    delete newAppliedFilters[key];
    setAppliedFilters(newAppliedFilters);
    
    fetchDashboardData(newAppliedFilters);
  };

  const fetchDashboardData = async (appliedFilters: Record<string, string> = {}) => {
    try {
      setLoading(true);
      
      // Fetch dashboard statistics with filters
      const reportResponse = await reportsAPI.generateReport({
        facilityName: appliedFilters.facilityName || undefined,
        doctorName: appliedFilters.doctorName || undefined,
        startDate: appliedFilters.startDate || undefined,
        endDate: appliedFilters.endDate || undefined,
        reportType: 'summary'
      });

      if (reportResponse.success && reportResponse.data.type === 'summary') {
        const reportData = reportResponse.data.data;
        setStats({
          overall: {
            ...reportData.summary,
            lowReadmission: reportData.summary.lowRisk || reportData.summary.lowReadmission || 0,
            moderateReadmission: reportData.summary.moderateRisk || reportData.summary.moderateReadmission || 0,
            highReadmission: reportData.summary.highRisk || reportData.summary.highReadmission || 0
          },
          topFacilities: reportData.facilityBreakdown || [],
          doctorBreakdown: reportData.doctorBreakdown?.map((doctor: any) => ({
            ...doctor,
            lowReadmission: doctor.lowRisk || doctor.lowReadmission || 0,
            moderateReadmission: doctor.moderateRisk || doctor.moderateReadmission || 0,
            highReadmission: doctor.highRisk || doctor.highReadmission || 0
          })) || []
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback to basic statistics if reports API fails
      try {
        const statsResponse = await laceToolAPI.getStatistics();
        setStats(statsResponse.data);
      } catch (fallbackError) {
        console.error('Error fetching fallback statistics:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [facilitiesResponse, doctorsResponse] = await Promise.all([
        reportsAPI.getFacilities(),
        reportsAPI.getDoctors()
      ]);

      if (facilitiesResponse.success) {
        setFacilities(facilitiesResponse.data);
      }

      if (doctorsResponse.success) {
        setDoctors(doctorsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      // Use the authenticated API services consistently
      const assessmentsResponse = await authenticatedLaceToolAPI.getAssessments({ limit: 5 });
      const auditResponse = await auditLogAPI.getAuditLogs({ 
        limit: 10, 
        sortBy: 'timestamp', 
        sortOrder: 'desc' 
      });

      // Combine and format activities
      const activities: RecentActivityItem[] = [];

      // Add recent assessments
      assessmentsResponse.assessments.forEach((assessment: Assessment) => {
        activities.push({
          id: `assessment-${assessment.id}`,
          type: 'assessment',
          icon: <FileText size={16} />,
          text: `Assessment completed for ${assessment.patientName} at ${assessment.facilityName}`,
          time: formatDistanceToNow(new Date(assessment.createdAt), { addSuffix: true }),
          timestamp: assessment.createdAt,
          user: assessment.assessedBy
        });
      });

      // Add recent audit logs
      auditResponse.logs.forEach((log: AuditLog) => {
        activities.push({
          id: `audit-${log.id}`,
          type: 'audit',
          icon: getActivityIcon(log.action, log.resource),
          text: formatActivityText(log),
          time: formatDistanceToNow(new Date(log.timestamp), { addSuffix: true }),
          timestamp: log.timestamp,
          user: log.userName
        });
      });

      // Sort by timestamp and take the most recent 8
      const sortedActivities = activities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 8);

      setRecentActivity(sortedActivities);
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      // Fallback to static data if API fails
      setRecentActivity([
        {
          id: 'fallback-1',
          type: 'assessment',
          icon: <FileText size={16} />,
          text: 'New LACE assessment completed',
          time: '2 minutes ago',
          timestamp: new Date().toISOString(),
          user: 'System'
        },
        {
          id: 'fallback-2',
          type: 'audit',
          icon: <Activity size={16} />,
          text: 'Dashboard statistics updated',
          time: '5 minutes ago',
          timestamp: new Date().toISOString(),
          user: 'System'
        }
      ]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Debug: Check if we have a token
        const token = await getAccessToken();
        console.log('Dashboard: Token available:', !!token);
        if (token) {
          console.log('Dashboard: Token preview:', token.substring(0, 20) + '...');
        }
        
        // Use main API service for statistics (it has better token handling)
        const statsResponse = await laceToolAPI.getStatistics();
        setStats(statsResponse.data);

        // Fetch recent activity
        await Promise.all([
          fetchDashboardData(),
          fetchFilterOptions(),
          fetchRecentActivity()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <SkeletonDashboard />;
  }

  const readmissionDistributionData = [
    { name: 'Low Readmission', value: stats?.overall.lowReadmission || 0, color: COLORS.lowReadmission },
    { name: 'Moderate Readmission', value: stats?.overall.moderateReadmission || 0, color: COLORS.moderateReadmission },
    { name: 'High Readmission', value: stats?.overall.highReadmission || 0, color: COLORS.highReadmission }
  ];

  const facilityData = stats?.topFacilities.slice(0, 5).map(facility => ({
    name: facility._id.length > 15 ? facility._id.substring(0, 15) + '...' : facility._id,
    assessments: facility.count,
    avgScore: Math.round(facility.averageScore * 10) / 10
  })) || [];

  const doctorData = stats?.doctorBreakdown?.slice(0, 5).map(doctor => ({
    name: doctor._id.length > 15 ? doctor._id.substring(0, 15) + '...' : doctor._id,
    assessments: doctor.count,
    avgScore: Math.round(doctor.averageScore * 10) / 10,
    lowReadmission: doctor.lowReadmission,
    moderateReadmission: doctor.moderateReadmission,
    highReadmission: doctor.highReadmission
  })) || [];


  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeLogo 
          src="https://www.chcahealth.org/wp-content/uploads/2023/12/main-logop.png" 
          alt="CHCA Logo"
        />
        <WelcomeContent>
          <WelcomeTitle>Welcome to CHCA LACE Tool</WelcomeTitle>
          <WelcomeSubtitle>
            Monitor patient readmission risk assessments and healthcare analytics
          </WelcomeSubtitle>
        </WelcomeContent>
        <WelcomeActions>
          <DownloadButton onClick={handleDownloadDashboardPDF}>
            <Download size={16} />
            Download Dashboard PDF
          </DownloadButton>
        </WelcomeActions>
      </WelcomeSection>

      <FilterSection>
        <FilterHeader>
          <Filter size={20} />
          Dashboard Filters
        </FilterHeader>
        <FilterGrid>
          <FilterGroup>
            <FilterLabel>Facility</FilterLabel>
            <FilterSelect
              value={filters.facilityName}
              onChange={(e) => handleFilterChange('facilityName', e.target.value)}
            >
              <option value="">All Facilities</option>
              {facilities.map((facility) => (
                <option key={facility.id} value={facility.name}>
                  {facility.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Health Care Provider</FilterLabel>
            <FilterSelect
              value={filters.doctorName}
              onChange={(e) => handleFilterChange('doctorName', e.target.value)}
            >
              <option value="">All Health Care Providers</option>
              {doctors.map((doctor) => (
                <option key={doctor.name} value={doctor.name}>
                  {doctor.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Start Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              placeholder="Select start date"
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>End Date</FilterLabel>
            <FilterInput
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              placeholder="Select end date"
            />
          </FilterGroup>
        </FilterGrid>
        
        <FilterActions>
          <FilterButton type="button" onClick={applyFilters}>
            <Filter size={16} />
            Apply Filters
          </FilterButton>
          <ClearButton type="button" onClick={clearFilters}>
            <X size={16} />
            Clear All
          </ClearButton>
        </FilterActions>
        
        {Object.keys(appliedFilters).length > 0 && (
          <ActiveFilters>
            {Object.entries(appliedFilters).map(([key, value]) => (
              <ActiveFilterTag key={key}>
                {key}: {value}
                <RemoveFilterButton onClick={() => removeFilter(key)}>
                  <X size={12} />
                </RemoveFilterButton>
              </ActiveFilterTag>
            ))}
          </ActiveFilters>
        )}
      </FilterSection>

      <QuickActionsSection>
        <ChartTitle>
          <Target size={20} />
          Quick Actions
        </ChartTitle>
        <QuickActionsGrid>
          <QuickActionButton onClick={() => handleQuickAction('new-assessment')}>
            <QuickActionIcon>
              <Plus size={20} />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>New Assessment</QuickActionTitle>
              <QuickActionDescription>Create a new LACE assessment</QuickActionDescription>
            </QuickActionContent>
            <ArrowRight size={16} />
          </QuickActionButton>

          <QuickActionButton onClick={() => handleQuickAction('view-assessments')}>
            <QuickActionIcon>
              <Eye size={20} />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>View Assessments</QuickActionTitle>
              <QuickActionDescription>Browse all assessments</QuickActionDescription>
            </QuickActionContent>
            <ArrowRight size={16} />
          </QuickActionButton>

          <QuickActionButton onClick={() => handleQuickAction('audit-logs')}>
            <QuickActionIcon>
              <Shield size={20} />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Audit Logs</QuickActionTitle>
              <QuickActionDescription>View system activity logs</QuickActionDescription>
            </QuickActionContent>
            <ArrowRight size={16} />
          </QuickActionButton>

          <QuickActionButton onClick={() => handleQuickAction('export-data')}>
            <QuickActionIcon>
              <Download size={20} />
            </QuickActionIcon>
            <QuickActionContent>
              <QuickActionTitle>Download Dashboard PDF</QuickActionTitle>
              <QuickActionDescription>Export dashboard report as PDF</QuickActionDescription>
            </QuickActionContent>
            <ArrowRight size={16} />
          </QuickActionButton>
        </QuickActionsGrid>
      </QuickActionsSection>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)">
              <FileText size={24} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats?.overall.totalAssessments || 0}</StatValue>
          <StatLabel>Total Assessments</StatLabel>
          <StatChange data-positive="true">
            <TrendingUp size={12} />
            +12% this month
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
              <BarChart3 size={24} />
            </StatIcon>
          </StatHeader>
          <StatValue>{Math.round((stats?.overall.averageReadmissionProbability || 0) * 10) / 10} %</StatValue>
          <StatLabel>Average Readmission Probability</StatLabel>
          <StatChange>
            <TrendingUp size={12} />
            -0.3 from last month
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
              <AlertTriangle size={24} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats?.overall.highReadmission || 0}</StatValue>
          <StatLabel>High Readmission Risk Patients</StatLabel>
          <StatChange>
            <TrendingUp size={12} />
            +3 this week
          </StatChange>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
              <Users size={24} />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats?.topFacilities.length || 0}</StatValue>
          <StatLabel>Active Facilities</StatLabel>
          <StatChange data-positive="true">
            <TrendingUp size={12} />
            +1 new facility
          </StatChange>
        </StatCard>
      </StatsGrid>

      <MetricsGrid>
        <MetricCard>
          <MetricHeader>
            <MetricTitle>
              <BarChart3 size={16} />
              Min Score
            </MetricTitle>
          </MetricHeader>
          <MetricValue>{stats?.overall.minScore || 0}</MetricValue>
          <MetricSubtext>Lowest LACE score</MetricSubtext>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>
              <Target size={16} />
              Max Score
            </MetricTitle>
          </MetricHeader>
          <MetricValue>{stats?.overall.maxScore || 0}</MetricValue>
          <MetricSubtext>Highest LACE score</MetricSubtext>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>
              <Users size={16} />
              Active Health Care Providers
            </MetricTitle>
          </MetricHeader>
          <MetricValue>{stats?.doctorBreakdown?.length || 0}</MetricValue>
          <MetricSubtext>Unique providers</MetricSubtext>
        </MetricCard>

        <MetricCard>
          <MetricHeader>
            <MetricTitle>
              <Calendar size={16} />
              Readmission Distribution
            </MetricTitle>
          </MetricHeader>
          <MetricValue>{Math.round(((stats?.overall.highReadmission || 0) / (stats?.overall.totalAssessments || 1)) * 100)}%</MetricValue>
          <MetricSubtext>High readmission risk percentage</MetricSubtext>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>
            <BarChart3 size={20} />
            Assessments by Facility
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={facilityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="assessments" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <PieChart size={20} />
            Readmission Distribution
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={readmissionDistributionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {readmissionDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPieChart>
            </ResponsiveContainer>
          </ChartContainer>
          <ReadmissionDistributionGrid>
            <ReadmissionCard risk="low">
              <ReadmissionValue>{stats?.overall.lowReadmission || 0}</ReadmissionValue>
              <ReadmissionLabel>Low Readmission</ReadmissionLabel>
            </ReadmissionCard>
            <ReadmissionCard risk="moderate">
              <ReadmissionValue>{stats?.overall.moderateReadmission || 0}</ReadmissionValue>
              <ReadmissionLabel>Moderate Readmission</ReadmissionLabel>
            </ReadmissionCard>
            <ReadmissionCard risk="high">
              <ReadmissionValue>{stats?.overall.highReadmission || 0}</ReadmissionValue>
              <ReadmissionLabel>High Readmission</ReadmissionLabel>
            </ReadmissionCard>
          </ReadmissionDistributionGrid>
        </ChartCard>
      </ChartsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>
            <Users size={20} />
            Assessments by Health Care Provider
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={doctorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="assessments" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>
            <BarChart3 size={20} />
            Health Care Provider Readmission Distribution
          </ChartTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={doctorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="lowReadmission" stackId="a" fill="#10b981" />
                <Bar dataKey="moderateReadmission" stackId="a" fill="#f59e0b" />
                <Bar dataKey="highReadmission" stackId="a" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </ChartCard>
      </ChartsGrid>

      <RecentActivity>
        <ChartTitle>
          <Activity size={20} />
          Recent Activity
        </ChartTitle>
        <ActivityList>
          {recentActivity.length > 0 ? (
            recentActivity.map((activity) => (
              <ActivityItem key={activity.id}>
                <ActivityIcon>{activity.icon}</ActivityIcon>
                <ActivityContent>
                  <ActivityText>{activity.text}</ActivityText>
                  <ActivityMeta>
                    {activity.user && (
                      <>
                        <ActivityUser>{activity.user}</ActivityUser>
                        <span>•</span>
                      </>
                    )}
                    <ActivityTime>{activity.time}</ActivityTime>
                  </ActivityMeta>
                </ActivityContent>
              </ActivityItem>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <Activity size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No recent activity to display</p>
            </div>
          )}
        </ActivityList>
      </RecentActivity>
    </DashboardContainer>
  );
};

export default Dashboard;
